/**
 * Contact-field validation for public forms.
 *
 * Mirrors `server/src/utils/contact.js` — the server copy is the one that
 * actually decides; this exists so a visitor sees the problem next to the
 * field instead of as a toast after a round-trip. `type="tel"` validates
 * nothing in HTML, which is how alphabetic "phone numbers" were getting
 * through, so every rule here is explicit.
 */

export const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

/**
 * Reduce an Indian mobile number to its canonical 10 digits, or '' when
 * it isn't one. Accepts `9876543210`, `+91 98765 43210`, `098765...`.
 */
export const normalizeIndianMobile = (value) => {
  let d = digitsOnly(value);
  if (!d) return '';
  // Peel prefixes OUTSIDE-IN: international (00), then any domestic trunk
  // zeros, then the country code. Order matters — `091-98765-43210` carries
  // a trunk 0 in front of the 91, so checking for '91' first misses it.
  if (d.startsWith('00')) d = d.slice(2);
  while (d.length > 10 && d.startsWith('0')) d = d.slice(1);
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  return /^[6-9]\d{9}$/.test(d) ? d : '';
};

export const isValidIndianMobile = (value) => normalizeIndianMobile(value) !== '';

export const isValidEmail = (value) => {
  const v = String(value ?? '').trim();
  if (!v || v.length > 254) return false;
  if (/\s/.test(v) || v.includes('..')) return false;
  return /^[^@]+@[^@.]+(\.[^@.]+)+$/.test(v);
};

export const isValidName = (value, { min = 2, max = 120 } = {}) => {
  const v = String(value ?? '').trim();
  return v.length >= min && v.length <= max && /\p{L}/u.test(v);
};

/**
 * Keystroke filter for a phone input: digits plus the punctuation people
 * legitimately type in a number. Blocking letters as they type is far
 * clearer than accepting them and complaining on submit.
 */
export const sanitizePhoneInput = (value) =>
  String(value ?? '').replace(/[^\d+\-()\s]/g, '').slice(0, 20);

/**
 * Validate the marketing demo/enquiry form.
 * Returns `{ field: message }` — empty object means valid.
 *
 * @param {Record<string, string>} form
 * @param {{ isEnquiry?: boolean }} [options]
 * @returns {Record<string, string>}
 */
export const validateLeadForm = (form, { isEnquiry = false } = {}) => {
  /** @type {Record<string, string>} */
  const errors = {};
  if (!isValidName(form.name)) errors.name = 'Please enter your full name';
  if (!isValidName(form.school)) errors.school = 'Please enter your school name';
  if (!isValidEmail(form.email)) errors.email = 'Please enter a valid email address';
  if (!isValidIndianMobile(form.phone)) errors.phone = 'Enter a valid 10-digit mobile number';

  if (isEnquiry) {
    const msg = String(form.message ?? '').trim();
    if (msg.length < 10) errors.message = 'Please tell us a little more (at least 10 characters)';
    else if (msg.length > 2000) errors.message = 'Please keep your message under 2000 characters';
  } else if (!String(form.students ?? '').trim()) {
    errors.students = 'Please select your school size';
  }

  return errors;
};
